import { AbstractControl, ValidationErrors, Validators } from '@angular/forms';
import { ConnectionType } from '../../../API';
import { DataSourceCard, PolicyType } from './data-connection';
import {
  createDatabricksDataConnection,
  createRedshiftDataConnection,
  createS3DataConnection,
  createSFTPDataConnection,
  createSnowflakeDataConnection,
  TestAwsS3DataConnection,
  TestDatabricksDataConnection,
  TestDataConnection,
  TestRedshiftDataConnection,
  updateSFTPDataConnection,
} from '../../../graphql/mutations';
import {
  AT_LEAST_ONE_ALPHABET,
  ISO_UTC_TIMESTAMP_VALIDATOR,
  NO_WHITESPACE_ALLOWED,
  OBJECT_IDENTIFIER_REGEX,
  ONLY_NUMBERS,
} from '../../shared/utils/constants';

const pemPrivateKeyValidator = (control: AbstractControl): ValidationErrors | null => {
  const value = control.value?.trim();
  if (!value) {
    return { required: true };
  }

  const match = value.match(/^-----BEGIN ([A-Z ]*PRIVATE KEY)-----\r?\n([\s\S]+)\r?\n-----END \1-----$/);
  return match?.[2]?.trim() ? null : { pemPrivateKey: true };
};

export const DataSourceCards: DataSourceCard[] = [
  {
    key: ConnectionType.AWS_S3,
    imgSrc: 'assets/images/s3-bucket.png',
    alt: 'AWS S3',
    title: 'AWS S3',
    isDisabled: false,
    description: 'Amazon Simple Storage Service',
    formDetails: [
      {
        fieldType: 'text',
        fieldName: 'connectionName',
        validator: [
          Validators.required,
          Validators.pattern(AT_LEAST_ONE_ALPHABET),
          Validators.pattern(AT_LEAST_ONE_ALPHABET),
        ],
        defaultValue: '',
        label: 'Connection Name',
        placeholder: 'Connection Name',
        errorText: 'Please enter connection name',
        layout: 'half',
      },
      {
        fieldType: 'text',
        fieldName: 'awsAccountId',
        validator: [Validators.required, Validators.pattern(ONLY_NUMBERS)],
        defaultValue: '',
        label: 'AWS Account ID',
        placeholder: 'AWS Account ID',
        errorText: 'Please enter valid AWS Account ID. It should only be numbers',
        layout: 'half',
      },
      {
        fieldType: 'checkbox',
        fieldName: PolicyType.S3,
        validator: [Validators.requiredTrue],
        defaultValue: null,
        label: 'Attach S3 Bucket Policy to your bucket',
        errorText: 'This is required to proceed with connection',
      },
      {
        fieldType: 'checkbox',
        fieldName: PolicyType.GLUE,
        validator: [Validators.requiredTrue],
        defaultValue: null,
        label: 'Attach Glue Catalog Policy to your AWS Account',
        errorText: 'This is required to proceed with connection',
      },
      {
        fieldType: 'checkbox',
        fieldName: PolicyType.KMS,
        validator: [Validators.requiredTrue],
        defaultValue: null,
        label: 'Configure KMS Decrypt permissions',
        errorText: 'This is required to proceed with connection',
      },
    ],
    connectionDetailsText: 'AWS S3 Connection Details',
    formTemplateName: 'awsS3FormTpl',
    graphqlQuery: createS3DataConnection,
    graphqlQueryName: 'createS3DataConnection',
    graphQlTestConnectionQuery: TestAwsS3DataConnection,
    graphQlTestConnectionQueryName: 'TestAwsS3DataConnection',
  },
  {
    key: ConnectionType.AWS_REDSHIFT,
    imgSrc: '/assets/images/redshift.png',
    alt: 'AWS Redshift',
    title: 'AWS Redshift',
    isDisabled: false,
    description: 'Amazon Redshift Data Warehouse',
    formDetails: [
      {
        fieldType: 'text',
        fieldName: 'connectionName',
        validator: [Validators.required, Validators.pattern(AT_LEAST_ONE_ALPHABET)],
        defaultValue: '',
        label: 'Connection Name',
        placeholder: 'Connection Name',
        errorText: 'Please enter connection name',
      },
      {
        fieldType: 'text',
        fieldName: 'databaseName',
        validator: [Validators.required],
        defaultValue: '',
        label: 'Database Name',
        placeholder: 'Database Name',
        errorText: 'Please enter database name',
      },
      {
        fieldType: 'text',
        fieldName: 'schemaName',
        validator: [Validators.required],
        defaultValue: '',
        label: 'Schema Name',
        placeholder: 'Schema Name',
        errorText: 'Please enter schema name',
      },
      {
        fieldType: 'text',
        fieldName: 'hostName',
        validator: [Validators.required],
        defaultValue: '',
        label: 'Host Name',
        placeholder: 'Host Name',
        errorText: 'Please enter host name',
      },
      {
        fieldType: 'text',
        fieldName: 'portNumber',
        validator: [Validators.required, Validators.pattern('^[0-9]*$')],
        defaultValue: '',
        label: 'Port Number',
        placeholder: 'Port Number',
        errorText: 'Please enter a valid port number',
      },
      {
        fieldType: 'text',
        fieldName: 'username',
        validator: [Validators.required],
        defaultValue: '',
        label: 'Username',
        placeholder: 'Username',
        errorText: 'Please enter username',
      },
      {
        fieldType: 'password',
        fieldName: 'password',
        validator: [Validators.required, Validators.pattern(AT_LEAST_ONE_ALPHABET)],
        defaultValue: '',
        label: 'Password',
        placeholder: 'Password',
        errorText: 'Please enter password',
      },
    ],
    connectionDetailsText: 'Redshift Connection Details',
    formTemplateName: 'awsRedShiftFormTpl',
    graphqlQuery: createRedshiftDataConnection,
    graphqlQueryName: 'createRedshiftDataConnection',
    graphQlTestConnectionQuery: TestRedshiftDataConnection,
    graphQlTestConnectionQueryName: 'TestRedshiftDataConnection',
  },
  {
    key: ConnectionType.SFTP,
    imgSrc: '/assets/images/sftp.png',
    alt: 'SFTP',
    title: 'SFTP',
    isDisabled: false,
    description: 'Secure File Transfer Protocol',
    formDetails: [
      {
        fieldType: 'text',
        fieldName: 'connectionName',
        validator: [Validators.required, Validators.pattern(AT_LEAST_ONE_ALPHABET)],
        defaultValue: '',
        label: 'Connection Name',
        placeholder: 'Connection Name',
        errorText: 'Please enter connection name',
        layout: 'full',
      },
      {
        fieldType: 'fileUpload',
        fieldName: 'fileName',
        validator: [],
        defaultValue: '',
        excludeFieldInForm: true,
        layout: 'full',
      },
    ],
    connectionDetailsText: 'Export Data to S3',
    formTemplateName: 'SFTPFormTpl',
    hideTest: true,
    graphqlQuery: createSFTPDataConnection,
    graphqlQueryName: 'createSFTPDataConnection',
    graphqlEditQuery: updateSFTPDataConnection,
    graphqlEditQueryName: 'updateSFTPDataConnection',
  },
  {
    key: ConnectionType.SNOWFLAKE,
    imgSrc: '/assets/images/snowflake_icon.png',
    alt: 'Snowflake',
    title: 'Snowflake',
    isDisabled: false,
    description: 'Snowflake Data Warehouse',
    formDetails: [
      {
        fieldType: 'text',
        fieldName: 'connectionName',
        validator: [Validators.required, Validators.pattern(AT_LEAST_ONE_ALPHABET)],
        defaultValue: '',
        label: 'Connection Name',
        placeholder: 'Connection Name',
        errorText: 'Please enter connection name',
      },
      {
        fieldType: 'text',
        fieldName: 'hostName',
        validator: [Validators.required, Validators.pattern('^[a-zA-Z0-9-]+(.[a-zA-Z0-9-]+)*.snowflakecomputing.com$')],
        defaultValue: '',
        label: 'Host Name',
        placeholder: "Host Name (e.g '*.snowflakecomputing.com'.)",
        errorText: 'Please enter valid Snowflake hostname.',
      },
      {
        fieldType: 'text',
        fieldName: 'portNumber',
        validator: [Validators.required, Validators.pattern('^[0-9]*$')],
        defaultValue: '',
        label: 'Port Number',
        placeholder: 'Port Number',
        errorText: 'Please enter a valid port number',
      },
      {
        fieldType: 'text',
        fieldName: 'warehouseName',
        validator: [Validators.required, Validators.pattern(OBJECT_IDENTIFIER_REGEX)],
        defaultValue: '',
        label: 'Warehouse Name',
        placeholder: 'Warehouse Name is Case Sensitive',
        errorText: 'Please enter valid warehouse name',
      },
      {
        fieldType: 'text',
        fieldName: 'databaseName',
        validator: [Validators.required, Validators.pattern(OBJECT_IDENTIFIER_REGEX)],
        defaultValue: '',
        label: 'Database Name',
        placeholder: 'Database Name is Case Sensitive',
        errorText: 'Please enter valid database name',
      },
      {
        fieldType: 'text',
        fieldName: 'schemaName',
        validator: [Validators.required, Validators.pattern(OBJECT_IDENTIFIER_REGEX)],
        defaultValue: '',
        label: 'Schema Name',
        placeholder: 'Schema Name is Case Sensitive',
        errorText: 'Please enter valid schema name',
      },
      {
        fieldType: 'text',
        fieldName: 'roleName',
        validator: [Validators.required, Validators.pattern(OBJECT_IDENTIFIER_REGEX)],
        defaultValue: '',
        label: 'Role Name',
        placeholder: 'Role Name is Case Sensitive',
        errorText: 'Please enter valid role name',
      },
      {
        fieldType: 'text',
        fieldName: 'sfUser',
        validator: [Validators.required, Validators.pattern(OBJECT_IDENTIFIER_REGEX)],
        defaultValue: '',
        label: 'Service Account Username',
        placeholder: 'Username is Case Sensitive',
        errorText: 'Please enter valid username',
      },
      {
        fieldType: 'textarea',
        fieldName: 'pem_private_key',
        validator: [pemPrivateKeyValidator],
        defaultValue: '',
        label: 'Private Key',
        placeholder: '-----BEGIN PRIVATE KEY-----',
        errorText: 'Please enter a valid PEM private key with matching BEGIN/END headers and a non-empty body',
        layout: 'full',
      },
    ],
    connectionDetailsText: 'Snowflake Connection Details',
    formTemplateName: 'snowflakeFormTpl',
    graphqlQuery: createSnowflakeDataConnection,
    graphqlQueryName: 'createSnowflakeDataConnection',
    graphQlTestConnectionQuery: TestDataConnection,
    graphQlTestConnectionQueryName: 'TestDataConnection',
  },
  {
    key: ConnectionType.DATABRICKS,
    imgSrc: '/assets/images/databricks-icon.png',
    alt: 'Databricks',
    title: 'Databricks',
    isDisabled: false,
    description: 'Databricks Data Warehouse',
    formDetails: [
      {
        fieldType: 'text',
        fieldName: 'connectionName',
        validator: [Validators.required, Validators.pattern(AT_LEAST_ONE_ALPHABET)],
        defaultValue: '',
        label: 'Connection Name',
        placeholder: 'Connection Name',
        errorText: 'Please enter connection name.',
      },
      {
        fieldType: 'text',
        fieldName: 'shareCredentialsVersion',
        validator: [Validators.required, Validators.pattern(ONLY_NUMBERS)],
        defaultValue: '',
        label: 'Share Credentials Version',
        placeholder: 'Share Credentials Version',
        errorText: 'Please enter valid share credentials version.',
      },
      {
        fieldType: 'text',
        fieldName: 'bearerToken',
        validator: [Validators.required, Validators.pattern(NO_WHITESPACE_ALLOWED)],
        defaultValue: '',
        label: 'Bearer Token',
        placeholder: 'Bearer Token',
        errorText: 'Please enter valid bearer token.',
      },
      {
        fieldType: 'text',
        fieldName: 'expirationTime',
        validator: [Validators.required, Validators.pattern(ISO_UTC_TIMESTAMP_VALIDATOR)],
        defaultValue: '',
        label: 'Expiration Time',
        placeholder: 'Expiration Time',
        errorText: 'Please enter valid expiration time.',
      },
      {
        fieldType: 'text',
        fieldName: 'shareName',
        validator: [Validators.required, Validators.pattern(NO_WHITESPACE_ALLOWED)],
        defaultValue: '',
        label: 'Share Name',
        placeholder: 'Share Name',
        errorText: 'Please enter share name.',
      },
      {
        fieldType: 'text',
        fieldName: 'schemaName',
        validator: [Validators.required, Validators.pattern(OBJECT_IDENTIFIER_REGEX)],
        defaultValue: '',
        label: 'Schema Name',
        placeholder: 'Schema Name is Case Sensitive',
        errorText: 'Please enter valid schema name',
      },
      {
        fieldType: 'text',
        fieldName: 'endpoint',
        validator: [Validators.required],
        defaultValue: '',
        label: 'Endpoint',
        placeholder: 'Endpoint',
        errorText: 'Please enter valid endpoint.',
      },
    ],
    connectionDetailsText: 'Databricks Connection Details',
    formTemplateName: 'databricksFormTpl',
    graphqlQuery: createDatabricksDataConnection,
    graphqlQueryName: 'createDatabricksDataConnection',
    graphQlTestConnectionQuery: TestDatabricksDataConnection,
    graphQlTestConnectionQueryName: 'TestDatabricksDataConnection',
  },
];
